import { useState, useEffect } from 'react';
import useSWR from 'swr';
import isEqual from 'lodash-es/isEqual';

import { IResponseError } from '@/lib/apiBase';
import { IRepository, IResourceVersion } from '@/types';
import { fetchQuery, TDfQuery, TQueryRequest, IDfQueryResponse, IQueryQuotaUsage } from '@/lib/dataframeQueryApi';

interface useDataframeQueryParams {
  repository: IRepository;
  resource: IResourceVersion;
  queryId?: string;
  initialData?: IDfQueryResponse;
}

const getRequestFromInitialData = (initialData?: IDfQueryResponse): TQueryRequest | undefined => {
  if (initialData?.query?.id) {
    return { type: 'id', value: initialData?.query?.id };
  }
  return undefined;
};

const useDataframeQuery = ({ repository, resource, initialData, queryId }: useDataframeQueryParams) => {
  const [status, setStatus] = useState<'success' | 'error' | 'pending'>('pending');
  const [error, setError] = useState<IResponseError | undefined>();
  const [query, setQuery] = useState<TDfQuery | undefined>(initialData?.query);
  const [queryQuotaUsage, setQueryQuotaUsage] = useState<IQueryQuotaUsage | undefined>(initialData?.query_quota_usage);

  const initialRequest: TQueryRequest | undefined = queryId
    ? { type: 'id', value: queryId }
    : getRequestFromInitialData(initialData);

  const [request, setRequest] = useState<TQueryRequest | undefined>(initialRequest);

  const fetchKey = [
    'dataframe_query',
    repository.namespace,
    repository.name,
    resource.version,
    resource.path,
    request?.type,
    request?.value,
  ];

  const {
    data,
    error: requestError,
    mutate,
    isLoading,
    isValidating,
  } = useSWR(fetchKey, () => fetchQuery({ repository, resource, request }), {
    revalidateOnMount: !initialData,
    keepPreviousData: true,
    revalidateOnFocus: false,
    fallbackData: initialData,
  });

  useEffect(() => {
    if (request && !isEqual(request, initialRequest)) {
      mutate();
    }
  }, [request]);

  useEffect(() => {
    if (data) {
      setQuery(data?.query);
      setQueryQuotaUsage(data?.query_quota_usage);
      setStatus(data?.status);
      setError(data?.error as IResponseError);
    }
  }, [data]);

  const fetchQueryById = (queryId?: string) => {
    if (!queryId) {
      console.error('fetchQueryById > queryId is undefined');
      return;
    }
    setRequest({ type: 'id', value: queryId });
  };

  const fetchQueryByNlp = (nlp?: string, refetch = false) => {
    if (!nlp) {
      console.error('fetchQueryByNlp > nlp is undefined');
      return;
    }
    setRequest({ type: 'nlp', value: nlp, refetch });
  };

  const fetchQueryBySql = (sql?: string) => {
    if (!sql) {
      console.error('fetchQueryBySql > sql is undefined');
      return;
    }
    setRequest({ type: 'sql', value: sql, parentId: query?.id });
  };

  const clearQuery = () => {
    setQuery(undefined);
    setQueryQuotaUsage(undefined);
    setStatus('pending');
    setError(undefined);
  };

  return {
    status,
    isLoading,
    isValidating,
    query,
    queryQuotaUsage,
    error,
    fetchQueryById,
    fetchQueryByNlp,
    fetchQueryBySql,
    clearQuery,
  };
};

export default useDataframeQuery;
